import React, { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { dataLayerPush } from '@r1-frontend/analytics/dataLayerPush';

import { btnTypes } from '@r1-frontend/ui-react/components/buttons/baseButton';
import { Label } from '@r1-frontend/ui-react/components/label';
import { COLORS } from '@r1-frontend/ui-react/tokens/colors';
import { CATEGORIES, EVENTS } from '@r1-frontend/shared/constants/analytics';

import { dataCameraPlans } from '~/src/features/videocontrol/data';
import { checkSaleInCity } from '~/src/features/videocontrol/pageBlocks/StepsInstall/utils';
import type { TPlan } from '~/src/features/videocontrol/Provider/types';
import { useRequisitionContext } from '~/src/features/videocontrol/Provider/useRequisitionContext';
import type { IDataPlans } from '~/src/features/videocontrol/types';
import { selectCurrentCityDomain } from '~/src/store/selectors/city';

import * as ST from './styled';

export const PlanCard: FC<{ product: IDataPlans }> = ({ product })=> {
    const currentCity = useSelector(selectCurrentCityDomain);
    const formattedProduct = useMemo((): IDataPlans => {
        if (!checkSaleInCity(currentCity)) {
            return { ...product, oldPrice: null, labelText: undefined, price: product.oldPrice || product.price };
        }
        return product;
    }, [currentCity, product]);

    const { plan, setPlanDispatch, setOptionDispatch } = useRequisitionContext();

    const onClickHandler = () => {
        const [cameraPlanFifty] = dataCameraPlans;
        if (plan?.id === formattedProduct.id) {
            setPlanDispatch(null);
            setOptionDispatch(null);
            return;
        }

        if (plan?.id === cameraPlanFifty.id) {
            setOptionDispatch(null);
        }

        const payload: TPlan = {
            name: formattedProduct.title,
            id: formattedProduct.id,
            currencyType: '₽/мес',
            priceMonth: formattedProduct.price,
            priceMonthWithCount: formattedProduct.priceMonthWithCount,
            oldPrice: formattedProduct.oldPrice,
            subService: formattedProduct.panelText,
            service: 'Ежемесячный платёж',
        };
        dataLayerPush({
            event: EVENTS.UAevent,
            category: CATEGORIES.landing,
            action: 'chose tariff',
            label: formattedProduct.title,
        });
        setPlanDispatch(payload);
    };

    return (
        <ST.Container key={formattedProduct.id}>
            <ST.TitleWrap>
                <ST.Title data-test={'title'}>{formattedProduct.title}</ST.Title>
                <ST.SubText data-test={'sub-text'}>{formattedProduct.subText}</ST.SubText>
            </ST.TitleWrap>
            <ST.PlankWrap>
                <ST.Plank data-test={'plank'}>
                    {formattedProduct.panelText}
                </ST.Plank>
                <ST.UnorderedList>
                    {formattedProduct.propsText?.map((elem, key) => (
                        <ST.List data-test={'list'} key={key}>{elem}</ST.List>
                    ))}
                </ST.UnorderedList>
            </ST.PlankWrap>
            <ST.Hr/>
            <ST.BottomWrap>
                <ST.PriceWrap>
                    <ST.Price data-test={'price'}>
                        {formattedProduct.price} ₽/мес
                    </ST.Price>
                    {formattedProduct.oldPrice &&
                        <ST.OldPrice data-test={'old-price'}>
                            {formattedProduct.oldPrice} ₽/мес
                        </ST.OldPrice>
                    }
                </ST.PriceWrap>
                {formattedProduct.labelText && formattedProduct.labelColor &&
                    <Label
                        data-test={'label'}
                        bgColor={formattedProduct.labelColor}
                        textColor={COLORS.TextWhite}
                    >
                        {formattedProduct.labelText}
                    </Label>
                }
                <ST.ButtonWrap>
                    <ST.Button
                        data-test={'button'}
                        type={btnTypes.WHITE}
                        text={formattedProduct.id === plan?.id ? 'Тариф выбран' : 'Выбрать тариф'}
                        isActive={formattedProduct.id === plan?.id}
                        onClick={onClickHandler}
                    />
                </ST.ButtonWrap>
            </ST.BottomWrap>
        </ST.Container>
    );
};
